import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AutocompleteInput } from '@/components/ui/autocomplete-input';
import { Label } from '@/components/ui/label';
import Field from './Field';
import { otrosTramitesService } from '@/otros-tramites/services/otrosTramitesService';
import type { UserOption } from '@/otros-tramites/services/otrosTramitesService';

interface CompradorVendedorData {
  nombre: string;
  email: string;
  dni: string;
  telefono: string;
}

interface CompradorVendedorFieldWithAutocompleteProps {
  value: CompradorVendedorData;
  onChange: (value: CompradorVendedorData) => void;
}

const CompradorVendedorFieldWithAutocomplete: React.FC<CompradorVendedorFieldWithAutocompleteProps> = ({
  value,
  onChange,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [localData, setLocalData] = useState<CompradorVendedorData>({
    nombre: '',
    email: '',
    dni: '',
    telefono: '',
  });

  // Initialize local data from props
  useEffect(() => {
    if (value) {
      setLocalData(value);
      if (value.nombre) {
        setSearchTerm(value.nombre);
      }
    }
  }, [value]);

  // React Query for user search
  const usersQuery = useQuery({
    queryKey: ['users', 'search', searchTerm],
    queryFn: () =>
      otrosTramitesService.searchUsers({
        search: searchTerm,
        per_page: 10,
      }),
    enabled: searchTerm.length >= 2,
    staleTime: 1000 * 30, // 30 seconds cache
  });

  // Handle user selection from autocomplete
  const handleUserSelect = (user: UserOption | null) => {
    if (!user) return;

    const newValue: CompradorVendedorData = {
      nombre: user.nombre,
      email: user.email || '',
      dni: user.profile?.dni || user.dni || '',
      telefono: user.profile?.telefono || user.telefono || '',
    };

    setLocalData(newValue);
    setSearchTerm(user.nombre); // Update search term to show selected user's name
    onChange(newValue);
  };

  // Handle field changes
  const handleFieldChange = (fieldName: string, fieldValue: string) => {
    setLocalData((prevData) => {
      const newValue = {
        ...prevData,
        [fieldName]: fieldValue,
      };
      onChange(newValue);
      return newValue;
    });

    // Update search term only when nombre field changes
    if (fieldName === 'nombre') {
      setSearchTerm(fieldValue);
    }
  };

  // Handle Field component onChange (different signature)
  const handleFieldComponentChange = ({ name, value: fieldValue }: { name: string; value: string }) => {
    handleFieldChange(name, fieldValue);
  };

  return (
    <div className="space-y-3 p-2 border border-gray-100 rounded-lg bg-gray-50/30">
      {/* Autocomplete for nombre */}
      <div className="space-y-2">
        <Label htmlFor="nombre">Nombre y apellido</Label>
        <AutocompleteInput
          value={localData.nombre}
          onChange={(value) => handleFieldChange('nombre', value)}
          onSelect={handleUserSelect}
          options={usersQuery.data || []}
          getOptionLabel={(user) => `${user.nombre}${user.email ? ` | ${user.email}` : ''}`}
          placeholder="Buscar persona..."
          isLoading={usersQuery.isFetching}
          emptyMessage="No se encontraron personas"
        />
      </div>

      {/* Email field */}
      <div className="space-y-2">
        <Field
          type="text"
          name="email"
          label="Email"
          value={localData.email}
          onChange={handleFieldComponentChange}
        />
      </div>

      {/* DNI and Telefono fields */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <Field
          type="text"
          name="dni"
          label="DNI"
          value={localData.dni}
          onChange={handleFieldComponentChange}
        />
        <Field
          type="text"
          name="telefono"
          label="Teléfono"
          value={localData.telefono}
          onChange={handleFieldComponentChange}
        />
      </div>
    </div>
  );
};

export default CompradorVendedorFieldWithAutocomplete;
